import { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import Modal from "../ui/modal/Modal";
import { useTheme } from "../../theme/ThemeProvider";
import { HOME_ROUTE } from "../../routes/constant";
import {
  sendOTP,
  loginWithOTP,
  registerUser,
} from "../../store/slices/authSlice";
import MobileStep from "./steps/MobileStep";
import OtpStep from "./steps/OtpStep";
import RegisterStep from "./steps/RegisterStep";
import { FaLock } from "react-icons/fa";
import toast from "react-hot-toast";
import { trackMetaRegistration } from "../../utils/metaPixel";

const LoginContent = ({
  isModal = false,
  onClose,
  onSuccess,
  redirectOnSuccess = true,
  modalVisible,
  productData,
}) => {
  const dispatch = useDispatch();
  const { isDarkMode } = useTheme();
  const { loading } = useSelector((state) => state.auth);
  const navigate = useNavigate();
  const location = useLocation();

  const getReferrerUrl = () => {
    const params = new URLSearchParams(location.search);
    const redirect = params.get("redirect");
    return redirect ? decodeURIComponent(redirect) : null;
  };

  const referrerUrl = getReferrerUrl();
  const [step, setStep] = useState("mobile");
  const [mobile, setMobile] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isModal && !modalVisible) {
      resetForm();
    }
  }, [isModal, modalVisible]);

  const resetForm = () => {
    setStep("mobile");
    setMobile("");
    setIsSubmitting(false);
  };

  const handleSuccess = useCallback(() => {
    // A caller with a pending calculation must consume onSuccess BEFORE any
    // onClose cancellation. The caller owns closing its modal after success.
    if (onSuccess) return onSuccess();
    if (isModal && onClose) return onClose();
    if (redirectOnSuccess) navigate(referrerUrl || HOME_ROUTE);
  }, [isModal, onClose, onSuccess, redirectOnSuccess, navigate, referrerUrl]);

  const handleSendOTP = useCallback(
    async (mobileNumber) => {
      if (isSubmitting) return;
      setIsSubmitting(true);
      try {
        const result = await dispatch(sendOTP(mobileNumber));
        if (sendOTP.fulfilled.match(result)) {
          setMobile(mobileNumber);
          if (result.payload.user_exists === false) {
            setStep("register");
          } else {
            setStep("otp");
          }
          return { success: true };
        }
        toast.error(result.payload || "Failed to send OTP");
        return { error: result.payload };
      } finally {
        setIsSubmitting(false);
      }
    },
    [dispatch, isSubmitting],
  );

  const handleVerifyOTP = useCallback(
    async (mobileNumber, otp) => {
      if (isSubmitting) return;
      setIsSubmitting(true);
      try {
        const result = await dispatch(
          loginWithOTP({ mobile: mobileNumber, otp }),
        );
        if (loginWithOTP.fulfilled.match(result)) {

          toast.success("Login successful!");
          handleSuccess();
          return { success: true };
        }
        toast.error(result.payload || "Invalid OTP");
        return { error: result.payload };
      } finally {
        setIsSubmitting(false);
      }
    },
    [dispatch, handleSuccess, isSubmitting],
  );

  const handleRegister = useCallback(
    async (userData) => {
      if (isSubmitting) return;
      setIsSubmitting(true);
      try {
        const result = await dispatch(registerUser(userData));
        if (registerUser.fulfilled.match(result)) {
          trackMetaRegistration(result.payload?.user?.id);
          toast.success("Registration successful!");
          handleSuccess();
          return { success: true };
        }
        toast.error(result.payload || "Registration failed");
        return { error: result.payload };
      } finally {
        setIsSubmitting(false);
      }
    },
    [dispatch, handleSuccess, isSubmitting],
  );

  const handleResendOTP = useCallback(async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      await dispatch(sendOTP(mobile));
      toast.success("OTP resent successfully");
    } catch (error) {
      toast.error("Failed to resend OTP");
    } finally {
      setIsSubmitting(false);
    }
  }, [dispatch, mobile, isSubmitting]);

  const renderStep = () => {
    switch (step) {
      case "mobile":
        return (
          <MobileStep
            onSubmit={handleSendOTP}
            loading={loading || isSubmitting}
            isDarkMode={isDarkMode}
          />
        );
      case "otp":
        return (
          <OtpStep
            mobile={mobile}
            onSubmit={handleVerifyOTP}
            onResend={handleResendOTP}
            onBack={() => setStep("mobile")}
            loading={loading || isSubmitting}
            isDarkMode={isDarkMode}
          />
        );
      case "register":
        return (
          <RegisterStep
            mobile={mobile}
            onSubmit={handleRegister}
            onResend={handleResendOTP}
            onBackToOtp={() => {
              localStorage.removeItem("otp_token");
              setStep("otp");
            }}
            onUpdateMobile={() => setStep("mobile")}
            loading={loading || isSubmitting}
            isDarkMode={isDarkMode}
          />
        );
      default:
        return null;
    }
  };

  const getTitle = () => {
    switch (step) {
      case "mobile":
        return "Login / Sign Up";
      case "otp":
        return "Verify OTP";
      case "register":
        return "Complete Registration";
      default:
        return "Login";
    }
  };

  if (!isModal) {
    return (
      <div className="flex items-center justify-center px-4 py-6">
        <div
          className={`max-w-md w-full rounded-2xl shadow-lg overflow-hidden ${isDarkMode ? "bg-gray-800" : "bg-white"}`}
        >
          <div className="p-6">
            <div className="mb-6 text-center">
              <h1
                className={`text-2xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                {getTitle()}
              </h1>
              <p
                className={`text-sm mt-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
              >
                {step === "mobile"
                  ? "Login or create an account"
                  : step === "register"
                    ? "Complete your profile"
                    : "Enter the verification code"}
              </p>
            </div>
            {renderStep()}
          </div>
        </div>
      </div>
    );
  }

  return (
    <Modal
      visible={modalVisible}
      onClose={onClose}
      title={getTitle()}
      size="sm"
      className="p-0"
    >
      {productData && step === "mobile" && (
        <>
          <div className="grid grid-cols-3 gap-3 px-[10px] py-2 border">
            <div className="overflow-hidden rounded-md">
              <img
                src={productData.product_image}
                alt={productData.product_name}
              />
            </div>
            <div className="col-span-2">
              <h2
                className={`text-xs font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                {productData.variation_name}
              </h2>
              <div className="flex flex-col">
                <p className="text-xs">Selling Price</p>
                <p
                  className={`text-2xl font-bold ${isDarkMode ? "text-orange-600" : "text-orange-600"}`}
                >
                  ₹{"XX, XXX"}
                </p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 p-2 my-2 bg-blue-200 rounded-md">
            <FaLock className="text-blue-600" />
            <p className="text-sm font-semibold text-blue-600">
              Login to Unlock the best price
            </p>
          </div>
        </>
      )}
      {renderStep()}
    </Modal>
  );
};

const LoginModal = ({
  visible,
  onClose,
  onSuccess,
  isPage = false,
  productData,
}) => {
  if (isPage) return <LoginContent isModal={false} onSuccess={onSuccess} />;
  return (
    <LoginContent
      productData={productData}
      isModal={true}
      modalVisible={visible}
      onClose={onClose}
      onSuccess={onSuccess}
    />
  );
};

export default LoginModal;
